<template>
  <div 
    :class="['message', { 
      'message-sent': isSent, 
      'message-received': !isSent,
      'message-deleted': message.deletedAt,
      'message-reply': hasReply
    }]"
    :id="`message-${message._id}`"
  >
    <!-- Reply Preview -->
    <div 
      v-if="hasReply && repliedMessage" 
      class="reply-preview"
      @click="scrollToMessage(message.replyingMessageId)"
    >
      <div class="reply-bar"></div>
      <div class="reply-content">
        <div class="reply-author">{{ getRepliedAuthor }}</div>
        <div class="reply-text">{{ getRepliedPreview }}</div>
      </div>
    </div>

    <!-- Deleted Message -->
    <div v-if="message.deletedAt" style="min-width: 24px;height: 24px;" class="deleted-content">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="15" y1="9" x2="9" y2="15"/>
        <line x1="9" y1="9" x2="15" y2="15"/>
      </svg>
      <span>This message was deleted</span>
    </div>

    <!-- Call Message -->
    <div v-else-if="message.callTime !== null" class="call-content">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
      <div class="call-info">
        <span v-if="message.callTime === 0" class="call-failed">
          {{ isSent ? 'Outgoing call' : 'Incoming call' }} • Missed
        </span>
        <span v-else class="call-success">
          {{ isSent ? 'Outgoing call' : 'Incoming call' }} • {{ formatDuration(message.callTime) }}
        </span>
      </div>
    </div>

    <!-- Regular Message Content -->
    <div v-else class="message-content">
      <!-- Image -->
      <div v-if="message.img" class="message-image" @click="$emit('viewImage', message.img)">
        <img :src="this.baseUrl+message.img" :alt="message.text || 'Image'" />
      </div>

      <!-- Audio -->
      <div v-if="message.audio" class="message-audio">
        <button @click="toggleAudio" class="audio-play-btn">
          <svg v-if="!isPlaying" style="min-width: 20px;height: 20px;" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3"/>
          </svg>
          <svg v-else style="min-width: 20px;height: 20px;" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16"/>
            <rect x="14" y="4" width="4" height="16"/>
          </svg>
        </button>
        
        <div class="audio-progress">
          <!-- Progress Bar (clickable) -->
          <div class="audio-progress-bar" @click="seekAudio">
            <div class="audio-progress-track">
              <div class="audio-progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              <div class="audio-progress-handle" :style="{ left: progressPercentage + '%' }"></div>
            </div>
          </div>
          
          <!-- Time Display -->
          <div class="audio-time-display">
            <span class="audio-current-time">{{ formattedCurrentTime }}</span>
            <span class="audio-separator">/</span>
            <span class="audio-total-time">{{ formattedDuration }}</span>
          </div>
        </div>
      </div>

      <!-- Text -->
      <div v-if="message.text" class="message-text">{{ message.text }}</div>

      <!-- Message Info -->
      <div class="message-info">
        <span v-if="message.modifiedAt" class="edited-label">Edited</span>
        <span class="message-time">{{ formatTime(message.createdAt) }}</span>
        
        <!-- Loading state -->
        <div v-if="isSent && isLoading" class="message-loader">
          <div class="loader-spinner"></div>
        </div>
        
        <!-- Seen/Unseen state (only when not loading) -->
        <template v-else-if="isSent">
          <!-- Double check for seen -->
          <svg v-if="message.seenAt" class="seen-icon seen-double" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"/>
            <polyline points="23 6 12 17"/>
          </svg>
          <!-- Single check for delivered but not seen -->
          <svg v-else class="seen-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </template>
      </div>
    </div>

    <!-- FIXED: Message Actions - show for ALL messages (sent and received) -->
    <div v-if="!message.deletedAt" class="message-actions">
      <button @click="$emit('reply', message)" class="action-btn">
        <svg style="min-width: 18px;height: 18px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 14 4 9 9 4"/>
          <path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
        </svg>
      </button>
      <!-- Only show edit/delete for sent messages -->
      <template v-if="isSent">
        <button @click="$emit('edit', message)" class="action-btn">
          <svg style="min-width: 18px;height: 18px;"  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        <button @click="$emit('delete', message)" class="action-btn delete-btn">
          <svg style="min-width: 18px;height: 18px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Message',
  props: {
    message: {
      type: Object,
      required: true
    },
    currentUserId: {
      type: String,
      required: true
    },
    allMessages: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isPlaying: false,
      audioElement: null,
      baseUrl: this.$store.state.baseUrl + 'images/',
      // ADD THESE
      currentTime: 0,
      duration: 0,
      progressUpdateInterval: null
    }
  },
  computed: {
    isSent() {
      return this.message.senderId === this.currentUserId;
    },
    hasReply() {
      return !!this.message.replyingMessageId;
    },
    repliedMessage() {
      if (!this.hasReply) return null;
      return this.allMessages.find(m => m._id === this.message.replyingMessageId);
    },
    getRepliedAuthor() {
      if (!this.repliedMessage) return '';
      return this.repliedMessage.senderId === this.currentUserId ? 'You' : 'Them';
    },
    getRepliedPreview() {
      if (!this.repliedMessage) return '';
      if (this.repliedMessage.text) return this.repliedMessage.text;
      if (this.repliedMessage.img) return '📷 Photo';
      if (this.repliedMessage.audio) return '🎵 Audio';
      if (this.repliedMessage.callTime !== null) return '📞 Call';
      return 'Message';
    },
    // ADD THIS
    progressPercentage() {
      if (!this.duration) return 0;
      return (this.currentTime / this.duration) * 100;
    },
    // ADD THIS
    formattedCurrentTime() {
      return this.formatDuration(Math.floor(this.currentTime));
    },
    // ADD THIS
    formattedDuration() {
      return this.formatDuration(this.duration || this.message.duration || 0);
    }
  },
  watch: {
    '$store.state.chatSetting.visible'(newVal) {
      if(!newVal) { // closing chat
        this.stopAudio()
      }
    }
  },
  methods: {
    scrollToMessage(messageId) {
      this.$emit('scrollTo', messageId);
    },
    toggleAudio() {
      if (this.isPlaying) {
        this.pauseAudio();
      } else {
        this.playAudio();
      }
    },
    playAudio() {
      if (!this.message.audio) return;
      
      // Stop any existing audio
      this.stopAudio();
      
      this.audioElement = new Audio(this.$store.state.baseUrl + 'audios/' + this.message.audio);
      
      // Set up event listeners
      this.audioElement.addEventListener('loadedmetadata', () => {
        this.duration = this.audioElement.duration;
      });
      
      this.audioElement.addEventListener('timeupdate', () => {
        this.currentTime = this.audioElement.currentTime;
      });
      
      this.audioElement.addEventListener('ended', () => {
        this.isPlaying = false;
        this.currentTime = 0;
        this.stopProgressUpdate();
      });
      
      this.audioElement.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        this.isPlaying = false;
        this.stopProgressUpdate();
      });
      
      // Play audio
      this.audioElement.play()
        .then(() => {
          this.isPlaying = true;
          this.startProgressUpdate();
        })
        .catch(err => {
          console.error('Playback error:', err);
          this.isPlaying = false;
        });
    },
    pauseAudio() {
      if (this.audioElement) {
        this.audioElement.pause();
        this.isPlaying = false;
        this.stopProgressUpdate();
      }
    },
    stopAudio() {
      if (!this.audioElement) return
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.isPlaying = false;
      this.currentTime = 0;
      this.stopProgressUpdate();
      //this.audioElement = null;
    },
    startProgressUpdate() {
      // Update progress more frequently for smoother animation
      this.progressUpdateInterval = setInterval(() => {
        if (this.audioElement) {
          this.currentTime = this.audioElement.currentTime;
        }
      }, 100); // Update every 100ms
    },
    stopProgressUpdate() {
      if (this.progressUpdateInterval) {
        clearInterval(this.progressUpdateInterval);
        this.progressUpdateInterval = null;
      }
    },
    seekAudio(event) {
      if (!this.audioElement || !this.duration) return;
      
      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * this.duration;
      
      this.audioElement.currentTime = newTime;
      this.currentTime = newTime;
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    formatDuration(seconds) {
      if (!seconds) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  },
  beforeUnmount() {
    this.stopAudio();
  }
}
</script>

<style scoped>
.message {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  max-width: 70%;
  min-height: fit-content;
  position: relative;
}

.message-sent     { align-self: flex-end;   align-items: flex-end; }
.message-received { align-self: flex-start; align-items: flex-start; }
.message-received .message-actions {
  right: auto;
  left: calc(100% + 8px);
}

.reply-preview {
  background: rgba(0, 212, 255, 0.1);
  border-left: 3px solid #00d4ff;
  padding: 8px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
}
.reply-preview > * + * { margin-left: 8px; }
.reply-preview:hover   { background: rgba(0, 212, 255, 0.2); }

.reply-bar {
  width: 3px;
  background: #00d4ff;
  border-radius: 2px;
}

.reply-content { flex: 1; min-width: 0; }

.reply-author {
  color: #00d4ff;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
}

.reply-text {
  color: #94a3b8;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-content {
  background: rgba(30, 41, 59, 0.8);
  border-radius: 12px;
  padding: 10px 14px;
  position: relative;
}
.message-sent .message-content {
  background: linear-gradient(135deg, #00d4ff, #0099cc);
}

.deleted-content {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: rgba(30, 41, 59, 0.4);
  border-radius: 12px;
  color: #94a3b8;
  font-style: italic;
  font-size: 14px;
}
.deleted-content > * + * { margin-left: 8px; }
.deleted-content svg     { width: 16px; height: 16px; }

.call-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(30, 41, 59, 0.6);
  border-radius: 12px;
}
.call-content > * + * { margin-left: 12px; }
.call-content svg     { width: 20px; height: 20px; color: #00d4ff; }

.call-info   { color: #e0f7ff; font-size: 14px; }
.call-failed { color: #ef4444; }
.call-success{ color: #22c55e; }

.message-image {
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 6px;
  max-width: 300px;
}
.message-image img { width: 100%; height: auto; display: block; }

.message-audio {
  display: flex;
  align-items: center;
  padding: 8px 0;
  min-width: 250px;
}
.message-audio > * + * { margin-left: 12px; }

.audio-play-btn {
  width: 40px;
  height: 40px;
  background: rgba(0, 212, 255, 0.2);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #00d4ff;
  flex-shrink: 0;
  transition: all 0.3s;
}
.audio-play-btn:hover { background: rgba(0, 212, 255, 0.3); transform: scale(1.05); }
.audio-play-btn svg   { width: 18px; height: 18px; }

.audio-progress {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.audio-progress > * + * { margin-top: 6px; }

.audio-progress-bar {
  cursor: pointer;
  padding: 4px 0;
}

.audio-progress-track {
  position: relative;
  height: 4px;
  background: rgba(0, 212, 255, 0.2);
  border-radius: 2px;
  overflow: visible;
}

.audio-progress-fill {
  position: absolute;
  top: 0; left: 0;
  height: 100%;
  background: #00d4ff;
  border-radius: 2px;
  transition: width 0.1s linear;
}

.audio-progress-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: #00d4ff;
  border: 2px solid #0a0e26;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s, transform 0.2s;
  pointer-events: none;
}
.audio-progress-bar:hover .audio-progress-handle  { opacity: 1; }
.audio-progress-bar:active .audio-progress-handle { transform: translate(-50%, -50%) scale(1.2); }

.audio-time-display {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
}
.audio-time-display > * + * { margin-left: 4px; }

.audio-current-time { color: #00d4ff; min-width: 35px; }
.audio-separator    { opacity: 0.5; }
.audio-total-time   { min-width: 35px; }

.message-sent .audio-play-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}
.message-sent .audio-play-btn:hover    { background: rgba(255, 255, 255, 0.3); }
.message-sent .audio-progress-track   { background: rgba(255, 255, 255, 0.3); }
.message-sent .audio-progress-fill    { background: white; }
.message-sent .audio-progress-handle  { background: white; border-color: rgba(0, 212, 255, 0.5); }
.message-sent .audio-time-display     { color: rgba(255, 255, 255, 0.7); }
.message-sent .audio-current-time     { color: white; }

.message-text {
  color: #e0f7ff;
  font-size: 15px;
  line-height: 1.5;
  word-wrap: break-word;
}
.message-sent .message-text { color: #ffffff; }

.message-info {
  display: flex;
  align-items: center;
  margin-top: 4px;
  justify-content: flex-end;
}
.message-info > * + * { margin-left: 6px; }

.edited-label {
  color: #94a3b8;
  font-size: 11px;
  font-style: italic;
}
.message-sent .edited-label { color: gray; }

.message-time { color: #94a3b8; font-size: 11px; }
.message-sent .message-time { color: rgba(255, 255, 255, 0.7); }

.message-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.loader-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.message-sent .loader-spinner {
  border-color: rgba(255, 255, 255, 0.3);
  border-top-color: rgba(255, 255, 255, 0.9);
}

.seen-icon          { width: 14px; height: 14px; color: rgba(255, 255, 255, 0.5); }
.seen-icon.seen     { color: #00d4ff; }
.seen-icon.seen-double { color: #00d4ff; }
.seen-icon.seen-double polyline:first-child { transform: translateX(-3px); }

.message-actions {
  position: absolute;
  top: 50%;
  right: calc(100% + 8px);
  transform: translateY(-50%);
  display: flex;
  opacity: 0;
  transition: opacity 0.3s;
}
.message-actions > * + * { margin-left: 4px; }
.message:hover .message-actions { opacity: 1; }

.action-btn {
  width: 32px;
  height: 32px;
  background: rgba(30, 41, 59, 0.9);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #e0f7ff;
  transition: all 0.3s;
}
.action-btn:hover  { background: rgba(0, 212, 255, 0.2); border-color: #00d4ff; }
.action-btn svg    { width: 16px; height: 16px; }

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
  color: #ef4444;
}
</style>